import type { TooltipContentProps, TooltipProps } from 'recharts';
import {
  TooltipCard,
  TooltipLabel,
  TooltipRow,
  SeriesDot,
  SeriesName,
  SeriesValue,
  PointTitle,
  PointSubtitle,
  PointSubtitleLabel,
} from './index.styles';
import { formatDateLabel, formatCompact } from '../../../../helpers';

type TooltipEntry = NonNullable<TooltipContentProps<number, string>['payload']>[number];

function resolveColor(entry: TooltipEntry): string | undefined {
  const datum = entry.payload as { fill?: string; color?: string } | undefined;

  return entry.color ?? datum?.fill ?? datum?.color;
}

function dedupeByDataKey(entries: readonly TooltipEntry[]): TooltipEntry[] {
  const byKey = new Map<string, TooltipEntry>();

  entries.forEach((entry) => {
    const key = String(entry.dataKey ?? entry.name);
    const existing = byKey.get(key);

    if (!existing || (!resolveColor(existing) && resolveColor(entry))) {
      byKey.set(key, entry);
    }
  });

  return Array.from(byKey.values());
}

interface ChartTooltipProps extends TooltipProps<number, string> {
  payloadFilter?: (entry: TooltipEntry) => boolean;
  valueFormatter?: (value: number) => string;
  hideIndicator?: boolean;
  getLabel?: (entry: TooltipEntry) => unknown;
  /** 'list' (default): one row per series — Area/Bar/Pie.
   *  'point': single annotated value — Scatter High/Low markers. */
  getTitle?: (entry: TooltipEntry) => unknown;
  variant?: 'list' | 'scatter' | 'heatmap' | 'pie';
  payload?: TooltipEntry[];
  label?: string | number;
}

export const ChartTooltip = ({
  active,
  payload,
  label,
  payloadFilter,
  valueFormatter = formatCompact,
  hideIndicator = false,
  getLabel,
  getTitle,
  variant = 'list',
}: ChartTooltipProps) => {
  if (!active || !payload?.length) return null;

  const filtered = payloadFilter ? payload.filter(payloadFilter) : payload;
  const rows = dedupeByDataKey(filtered);

  if (!rows.length) return null;

  const rawLabel = getLabel ? getLabel(rows[0]) : label;
  const formattedLabel = rawLabel !== undefined && rawLabel !== null ? formatDateLabel(rawLabel as never) : undefined;
  const showLabel = Boolean(formattedLabel) && formattedLabel !== 'Invalid DateTime';

  if (variant === 'scatter') {
    const entry = rows[0];
    const value = typeof entry.value === 'number' ? valueFormatter(entry.value) : entry.value;

    return (
      <TooltipCard>
        <PointTitle>{getTitle ? (getTitle(entry) as string) : entry.name}</PointTitle>
        <PointSubtitle>
          <PointSubtitleLabel>{showLabel ? `${formattedLabel}` : null}</PointSubtitleLabel>
          {showLabel ? ` · ${value}` : value}
        </PointSubtitle>
      </TooltipCard>
    );
  }

  if (variant === 'heatmap') {
    const title = getTitle ? (getTitle(rows[0]) as string) : (formattedLabel as string);
    const value = getLabel ? (getLabel(rows[0]) as string) : (rows[0].value as string);

    const formattedValue = typeof value === 'number' ? valueFormatter(value) : value;

    return (
      <TooltipCard>
        <PointTitle>{title}</PointTitle>
        <PointSubtitle>{formattedValue}</PointSubtitle>
      </TooltipCard>
    );
  }

  return (
    <TooltipCard>
      {showLabel && <TooltipLabel>{formattedLabel}</TooltipLabel>}
      {rows.map((entry, i) => {
        const color = hideIndicator ? undefined : resolveColor(entry);

        const rawEntryValue = entry.value;
        let value: string | number | undefined;

        if (typeof rawEntryValue === 'number') {
          value = variant === 'pie' ? `${valueFormatter(rawEntryValue * 100)}%` : valueFormatter(rawEntryValue);
        } else {
          value = rawEntryValue as string;
        }

        return (
          <TooltipRow key={String(entry.dataKey ?? entry.name ?? i)}>
            {color && <SeriesDot $color={color} />}
            <SeriesName>{entry.name}</SeriesName>
            <SeriesValue>{value}</SeriesValue>
          </TooltipRow>
        );
      })}
    </TooltipCard>
  );
};
